import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { readPanelState, writePanelState } from '../../../shared/panel-state/panel-session-storage';
import { SidebarComponent } from '../../../shared/sidebar/sidebar.component';

/**
 * Which view the shared left page panel is currently showing. The panel hosts
 * either the shift-templates/teams tabs or the schedule filters — never both at
 * once, so opening one takes over the panel from the other (SHB-20413).
 */
export type ScheduleSidebarContent = 'shifts-teams' | 'filters';

/** Persisted-per-session so returning to the schedule restores the panel the user left open. */
interface ScheduleSidebarState {
  open: boolean;
  content: ScheduleSidebarContent;
}

const surface = 'schedule';

@Injectable()
export class ScheduleSidebarService {
  sideBarStateChange = new Subject<boolean>();
  #sideBar: SidebarComponent;
  #departmentId$ = new BehaviorSubject<string>(null);

  #state: ScheduleSidebarState = readPanelState<ScheduleSidebarState>(surface) ?? {
    open: false,
    content: 'shifts-teams',
  };
  #content = signal<ScheduleSidebarContent>(this.#state.content);

  content = this.#content.asReadonly();

  setSideBar(sidebar: SidebarComponent): void {
    this.#sideBar = sidebar;
  }

  /**
   * Re-opens the panel on the view the user left open, used when returning to
   * the schedule after navigating away. No-op when the panel was closed — the
   * app should never decide which panel shows on return, only restore it.
   */
  restoreSideBar(): void {
    if (!this.#state.open) {
      return;
    }

    this.#sideBar.open();
    this.sideBarStateChange.next(true);
  }

  openSideBar(): void {
    this.#setContent('shifts-teams');
    this.#sideBar.open();
    this.#persist(true);
    this.sideBarStateChange.next(true);
  }

  closeSideBar(): void {
    this.#sideBar.close();
    this.#setContent('shifts-teams');
    this.#persist(false);
    this.sideBarStateChange.next(false);
  }

  openFilters(): void {
    this.#setContent('filters');
    this.#sideBar.open();
    this.#persist(true);
    this.sideBarStateChange.next(true);
  }

  toggleFilters(): void {
    if (this.isOpen() && this.#content() === 'filters') {
      this.closeSideBar();
      return;
    }

    this.openFilters();
  }

  isOpen(): boolean {
    return this.#sideBar && this.#sideBar.opened();
  }

  getSelectedDepartment(): BehaviorSubject<string> {
    return this.#departmentId$;
  }

  setDepartmentId(departmentId: string): void {
    this.#departmentId$.next(departmentId);
  }

  toggleSideBar(): void {
    this.#setContent('shifts-teams');
    this.#sideBar.toggle();
    this.#persist(this.#sideBar.opened());
    this.sideBarStateChange.next(this.#sideBar.opened());
  }

  get hasSidebar(): boolean {
    return !!this.#sideBar;
  }

  #setContent(content: ScheduleSidebarContent): void {
    this.#content.set(content);
  }

  #persist(open: boolean): void {
    this.#state = { open, content: this.#content() };
    writePanelState(surface, this.#state);
  }
}
